import React from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useMatch } from 'react-router-dom'
import {
  Box,
  Button,
  Callout,
  Checkbox,
  Combobox,
  Box as FormActions,
  Box as FormFields,
  Input,
  Input2FA,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { PathNames } from 'constants/General'
import { RelationshipType } from 'types/contacts'
import { contactAtom, contactInstrumentActionAtom } from './atoms/contactAtoms'
import { ContactCard } from './components/ContactCard'
import { useContactInstrumentForm } from './hooks/useContactInstrumentForm'

export const ContactInstrumentForm = () => {
  const { t } = useTranslation()
  const contactInstrumentAction = useAtomValue(contactInstrumentActionAtom)
  const contact = useAtomValue(contactAtom)
  const matchContact = useMatch(PathNames.CONTACT)
  const matchContacts = useMatch(PathNames.CONTACTS)
  const match = matchContact || matchContacts || null

  const {
    formFields,
    isLoadingCryptoCurrencies,
    errors,
    isSubmitting,
    isPostingContactInstrument,
    isPostingChallengeRequest,
    isEditingContactInstrument,
    isUpdatingContactInstrument,
    requestError,
    hasDestinationTag,
    isStellarMemo,
    handleSubmit,
    onSubmit,
    setValue,
    clearErrors,
  } = useContactInstrumentForm()

  const isLoadingSubmit = isSubmitting || isPostingChallengeRequest
  const isSubmittingAddContactInstrument = isLoadingSubmit || isPostingContactInstrument
  const isSubmittingUpdateContactInstrument = isLoadingSubmit || isUpdatingContactInstrument
  const isFirstParty = contact?.relationshipType === RelationshipType.SELF_OWNED

  return (
    <Box width="full">
      <form data-testid="contact-instrument-form" onSubmit={handleSubmit(onSubmit)}>
        <FormFields width="full" flexCol gapY={16} paddingT={24}>
          <ContactCard name={contact?.name} showAvatar={false} />
          {requestError?.show && <RequestErrorCallout requestError={requestError} />}
          <h3 className="text-h3 text-foreground-prominent">{t('contacts.accountDetails')}</h3>
          <Input
            testId="description"
            {...formFields.description}
            onChange={e => {
              setValue('description', e?.target?.value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
          <Combobox
            testid="currency-and-network"
            {...formFields.currencyAndNetwork}
            loading={isLoadingCryptoCurrencies}
          />
          <Input
            testId="wallet-address"
            {...formFields.walletAddress}
            onChange={e => {
              setValue('walletAddress', e?.target?.value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
          {hasDestinationTag && (
            <Input
              testId="tag"
              {...formFields.tag}
              label={`${isStellarMemo ? t('formFields.memo') : t('formFields.destinationTag')} ${t('formFields.optional')}`}
              onChange={e => {
                setValue('tag', e?.target?.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            />
          )}
          {isFirstParty &&
            (match ? (
              <Checkbox
                disabled={!match}
                {...formFields.selfHosted}
                label={match ? t('formFields.isSelfHosted') : t('contacts.selfHostedDisclaimer')}
              >
                {match && t('contacts.selfHostedExplanation')}
              </Checkbox>
            ) : (
              <Callout variant="warning">{t('contacts.selfHostedDisclaimer')}</Callout>
            ))}

          <Box>
            <Input2FA
              testid="otp"
              name="otp"
              {...formFields.otp}
              onChange={value => {
                setValue('otp', value)
                clearErrors('otp')
              }}
              error={!!errors && errors?.otp?.message}
            />
          </Box>
          <FormActions flex justify="between">
            <Button testId="cancel" variant="secondary" onClick={contactInstrumentAction?.onClose}>
              {t('cancel')}
            </Button>
            <Button
              testId={
                isEditingContactInstrument ? 'update-contact-instrument' : 'add-contact-instrument'
              }
              type="submit"
              variant="primary"
              loading={
                isEditingContactInstrument
                  ? isSubmittingUpdateContactInstrument
                  : isSubmittingAddContactInstrument
              }
              disabled={!!Object.keys(errors)?.length}
            >
              {isEditingContactInstrument ? t('contacts.updateAccount') : t('contacts.addAccount')}
            </Button>
          </FormActions>
        </FormFields>
      </form>
    </Box>
  )
}
